import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, RefreshCw, Wrench } from "lucide-react";
import { ROUTES } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Loader";
import { useAuth } from "@/hooks/useAuth";
import {
  extractApiError,
  fetchLocacoesSnapshot,
  REGISTRO_HORIMETRO_LABELS,
  MANUTENCAO_STATUS_LABELS,
  type LocacoesSnapshot,
} from "@/services/locacoes";
import { formatDate } from "@/utils/format";
import {
  filterMaquinasPesadas,
  minhasSolicitacoesHorimetro,
  minhasSolicitacoesManutencao,
} from "./operadorUtils";

const STATUS_BADGE: Record<string, string> = {
  pendente: "bg-amber-100 text-amber-800",
  aprovado: "bg-emerald-100 text-emerald-800",
  rejeitado: "bg-red-100 text-red-800",
};

export function OperadorHomePage() {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState<LocacoesSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLocacoesSnapshot();
      setSnapshot(data);
    } catch (e) {
      setError(extractApiError(e, "Erro ao carregar dados."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const maquinas = useMemo(
    () => filterMaquinasPesadas(snapshot?.maquinariosScoped ?? snapshot?.maquinarios ?? []),
    [snapshot],
  );

  const horimetros = useMemo(
    () => (user ? minhasSolicitacoesHorimetro(snapshot?.registrosHorimetro ?? [], user.id) : []),
    [snapshot, user],
  );

  const manutencoes = useMemo(
    () => (user ? minhasSolicitacoesManutencao(snapshot?.manutencoes ?? [], user.id) : []),
    [snapshot, user],
  );

  if (loading && !snapshot) {
    return <PageLoader label="Carregando..." />;
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <p className="text-sm text-slate-600">
          Olá, <span className="font-semibold text-slate-800">{user?.displayName}</span>.
          Suas solicitações passam pela aprovação da engenharia antes de serem efetivadas.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          {maquinas.length} máquina{maquinas.length === 1 ? "" : "s"} disponível
          {maquinas.length === 1 ? "" : "is"} no seu escopo.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Link
          to={ROUTES.OPERADOR_HORIMETRO}
          className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-600 px-4 py-4 text-white shadow-card transition-opacity hover:opacity-95"
        >
          <Clock className="h-6 w-6 shrink-0" />
          <div>
            <p className="font-bold">Solicitar horímetro</p>
            <p className="text-xs text-brand-100">Foto do painel obrigatória</p>
          </div>
        </Link>
        <Link
          to={ROUTES.OPERADOR_MANUTENCAO}
          className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-slate-800 shadow-card transition-colors hover:bg-slate-50"
        >
          <Wrench className="h-6 w-6 shrink-0 text-amber-600" />
          <div>
            <p className="font-bold">Solicitar manutenção</p>
            <p className="text-xs text-slate-500">Descreva o problema</p>
          </div>
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-800">Minhas solicitações</h2>
        <Button type="button" variant="ghost" size="sm" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => void load()} loading={loading}>
          Atualizar
        </Button>
      </div>

      <div className="space-y-3">
        {horimetros.length === 0 && manutencoes.length === 0 && (
          <p className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
            Nenhuma solicitação registrada ainda.
          </p>
        )}

        {horimetros.slice(0, 8).map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800">{r.maquinaNome}</p>
                <p className="text-xs text-slate-500">
                  Horímetro • {REGISTRO_HORIMETRO_LABELS[r.tipo]} • {r.horimetroInformado} h •{" "}
                  {formatDate(r.dataHora.slice(0, 10))}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[r.statusAprovacao ?? "aprovado"] ?? "bg-slate-100 text-slate-600"}`}
              >
                {r.statusAprovacao ?? "—"}
              </span>
            </div>
            {r.motivoRejeicao && (
              <p className="mt-2 text-xs text-red-600">Motivo: {r.motivoRejeicao}</p>
            )}
          </div>
        ))}

        {manutencoes.slice(0, 8).map((m) => (
          <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800">{m.maquinaNome}</p>
                <p className="text-xs text-slate-500">
                  Manutenção • {m.tipo} • {formatDate(m.dataAbertura)}
                </p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-600">{m.descricao}</p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[m.statusAprovacao ?? "aprovado"] ?? "bg-slate-100 text-slate-600"}`}
              >
                {m.statusAprovacao ?? "—"}
              </span>
            </div>
            {m.motivoRejeicao && (
              <p className="mt-2 text-xs text-red-600">Motivo: {m.motivoRejeicao}</p>
            )}
            {m.statusAprovacao === "aprovado" && (
              <p className="mt-1 text-xs text-slate-500">Status: {MANUTENCAO_STATUS_LABELS[m.status]}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
